import { useState, useEffect, useMemo } from "react";
import { Sparkles, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * What's New changelog feed.
 * Bump the version string in the latest entry whenever you ship something user-facing.
 * Newest entry first.
 */
const WHATS_NEW = [
  {
    version: "2026.05.16g",
    date: "16 May 2026",
    title: "Quote visits go Overdue if not sent same day",
    badges: ["Quotes", "Dashboard"],
    items: [
      "Any Quote Booked visit that hasn't had its quote sent by 4pm on the booked date now auto-moves to Overdue",
      "Same rule that already applied to Job Booked and Recall Booked — keeps the pipeline honest",
      "Sending the quote at any point pulls it back out of Overdue automatically",
      "Timeline entry records exactly when and why the status flipped",
    ],
  },
  {
    version: "2026.05.16f",
    date: "16 May 2026",
    title: "Automated invoice chasers now actually send",
    badges: ["Invoices", "Email", "Fix"],
    items: [
      "Daily 1st & 2nd payment chase emails now dispatch directly via Resend with the full UK letting-agent template",
      "support@hammondpropertysolutions.co.uk is always CC'd on every chase (auto + manual)",
      "Invoice PDF is attached to every chase email automatically",
      "Idempotent — once a chase is marked sent, it never re-sends. Catch-up sends fire on first run for already-overdue invoices",
      "Failures show in the View Job Pack timeline so missing-data issues are visible",
    ],
  },
  {
    version: "2026.05.16e",
    date: "16 May 2026",
    title: "Mobile forced-update overlay fixed",
    badges: ["Mobile", "Platform", "Fix"],
    items: [
      "Update Required overlay now renders correctly on iPhone, Android and tablet browsers",
      "Service worker now bypasses cache for version.json so mobile users always get the true latest version",
      "Re-checks the version whenever the app comes back to the foreground — catches deploys that happened while the tab was backgrounded",
      "Tapping Update Now on iOS now reliably triggers a full cache-busted reload",
    ],
  },
  {
    version: "2026.05.16d",
    date: "16 May 2026",
    title: "Recall workflow fixed",
    badges: ["Mobile", "Fix"],
    items: [
      "Booking a recall now correctly resets the completion state — contractors can finish the recall visit without 'already completed' errors",
      "Actions completed on a previous visit show locked + green on the recall job sheet — contractors can't accidentally change them",
      "Photo requirement applies independently to the recall visit (1 photo per outstanding action this visit)",
      "Outstanding actions auto-carry into the recall sheet — no manual re-entry",
    ],
  },
  {
    version: "2026.05.16c",
    date: "16 May 2026",
    title: "Add Supplier — fixed and tightened",
    badges: ["Suppliers", "Fix"],
    items: [
      "New suppliers now appear immediately in the list (optimistic insert + server resync)",
      "Suppliers list is alphabetised — your new entry shows up where you expect, not at the bottom",
      "Add Supplier dialog now auto-focuses the input, clears on close, Enter submits, button disables until you type",
      "Same input pattern available for future Add Client work",
    ],
  },
  {
    version: "2026.05.16b",
    date: "16 May 2026",
    title: "Multiple-quote workflow fixed",
    badges: ["Quotes", "Fix"],
    items: [
      "Sending Option 2 now correctly sets Option 2 as active — the job no longer defaults back to Quote 1",
      "Job status, days outstanding, chase counter, and Dashboard pipeline all read from the active quote",
      "Active option shows an orange left border + green Active badge in the Multiple Quotes panel",
      "Active quote cannot be deleted while active; sending a non-active option asks for confirmation first",
    ],
  },
  {
    version: "2026.05.16",
    date: "16 May 2026",
    title: "Days outstanding no longer resets on resend",
    badges: ["Invoices", "Fix"],
    items: [
      "Resending an invoice or quote keeps the original sent date — days outstanding is preserved",
      "Chase counter and 1st/2nd Chase Due markers are anchored to the first sent date, not the most recent resend",
      "Hover the days column on Outstanding Payments to see both 'first sent' and 'last sent' timestamps",
      "All 127 existing invoiced records auto-migrated — no data lost, counters carry on as before",
    ],
  },
  {
    version: "2026.05.14d",
    date: "14 May 2026",
    title: "Forced update on every deploy",
    badges: ["Platform", "Stability"],
    items: [
      "After every deploy the app forces a fresh load — no more half-old versions running in different tabs",
      "Stale users see a clean 'Update Required' screen with one button — no way to bypass and no risk of working on old code",
      "Quietly re-checks every 30 minutes so long-running sessions also get caught",
    ],
  },
  {
    version: "2026.05.14c",
    date: "14 May 2026",
    title: "Documents section + upload bug fix",
    badges: ["Quotes", "Mobile", "Fix"],
    items: [
      "New Documents section on View Job Pack, Edit Quote and Mobile Edit Quote — PDFs, photos and office docs all accepted",
      "Mobile contractors can read internal documents from the job sheet (read-only)",
      "Fixed a backend bug where the upload category sent in form data was ignored — Material Purchases and Documents now save correctly",
      "All three media types stay strictly separated; Documents never appear on any PDF",
    ],
  },
  {
    version: "2026.05.14b",
    date: "14 May 2026",
    title: "Job Photos and Material Purchases — fully separated",
    badges: ["Quotes", "Mobile", "PDFs"],
    items: [
      "View Job Pack now shows two distinct cards: Job Photos (on PDFs) and Material Purchases (records only)",
      "Edit Quote has separate dropzones for job photos vs material receipts with explanatory text",
      "Mobile Contractor Job Sheet clearly separates the two sections with helper labels",
      "Material purchase receipts are server-side excluded from every PDF and from the Select Photos for Invoice modal",
    ],
  },
  {
    version: "2026.05.14",
    date: "14 May 2026",
    title: "Code quality & performance pass",
    badges: ["Performance", "Stability"],
    items: [
      "Faster Clients & Materials Catalogue rendering (memoised filter + sort)",
      "Photo upload + To-do load errors now surface a toast instead of failing silently",
      "Tightened action-completion check on the contractor job sheet",
    ],
  },
  {
    version: "2026.04.29",
    date: "29 Apr 2026",
    title: "Multi-visit bookings",
    badges: ["Calendar"],
    items: [
      "Add extra visit dates to the same job from the calendar edit panel",
      "Each booking gets its own contractor, times and weekend-skip rules",
      "Each visit shows as a separate bar on the calendar grid",
    ],
  },
  {
    version: "2026.04.26",
    date: "26 Apr 2026",
    title: "Per-contractor scheduling & primary quote",
    badges: ["Calendar", "Quotes"],
    items: [
      "Independent start/end dates and times per additional team member",
      "Skip Saturday, Sunday or both on multi-day jobs",
      "Pick the accepted quote when a job has multiple options — Dashboard now opens the right one",
    ],
  },
];

const STORAGE_KEY = "whats_new_last_seen_version";
const LATEST_VERSION = WHATS_NEW[0]?.version || "";

export function WhatsNewButton({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(
    typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) || "" : ""
  );

  const hasUnseen = useMemo(() => LATEST_VERSION && lastSeen !== LATEST_VERSION, [lastSeen]);

  useEffect(() => {
    if (open && LATEST_VERSION && lastSeen !== LATEST_VERSION) {
      localStorage.setItem(STORAGE_KEY, LATEST_VERSION);
      setLastSeen(LATEST_VERSION);
    }
  }, [open, lastSeen]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`group relative flex items-center justify-center rounded-lg transition-all duration-150 ${compact ? "w-8 h-8" : "w-10 h-10"}`}
        style={{ background: "transparent" }}
        title="What's new"
        data-testid="whats-new-btn"
        aria-label="What's new"
      >
        <Sparkles
          className={compact ? "w-4 h-4" : "w-5 h-5"}
          style={{ color: hasUnseen ? "#E05C2A" : "var(--text-muted)" }}
        />
        {hasUnseen && (
          <span
            className="absolute top-1 right-1 flex h-2 w-2"
            data-testid="whats-new-pulse"
          >
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#E05C2A" }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#E05C2A" }} />
          </span>
        )}
        {!compact && (
          <span className="absolute left-14 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
            What's new
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg p-0 overflow-hidden"
          data-testid="whats-new-dialog"
        >
          <DialogHeader className="px-6 pt-5 pb-4" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#FFF5F2" }}>
                <Sparkles className="w-4 h-4" style={{ color: "#E05C2A" }} />
              </div>
              <div>
                <DialogTitle className="text-base font-semibold m-0">What's new</DialogTitle>
                <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                  Recent improvements to HPS Portal
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-5">
            {WHATS_NEW.map((entry, idx) => (
              <div
                key={entry.version}
                className="relative pl-5"
                style={{ borderLeft: idx === 0 ? "2px solid #E05C2A" : "2px solid var(--border)" }}
                data-testid={`whats-new-entry-${entry.version}`}
              >
                <div
                  className="absolute -left-[5px] top-1 w-2 h-2 rounded-full"
                  style={{ background: idx === 0 ? "#E05C2A" : "var(--text-muted)" }}
                />
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                    {entry.date}
                  </span>
                  {idx === 0 && (
                    <span className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded" style={{ background: "#FFF5F2", color: "#E05C2A" }}>
                      New
                    </span>
                  )}
                  {(entry.badges || []).map(b => (
                    <span
                      key={b}
                      className="text-[9px] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded"
                      style={{ background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}
                    >
                      {b}
                    </span>
                  ))}
                </div>
                <h3 className="text-sm font-semibold mb-1.5" style={{ color: "var(--text-primary)" }}>
                  {entry.title}
                </h3>
                <ul className="space-y-1">
                  {entry.items.map(item => (
                    <li key={item} className="text-xs flex gap-2" style={{ color: "var(--text-secondary)" }}>
                      <span style={{ color: "var(--text-muted)" }}>—</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="px-6 py-3 flex justify-end" style={{ borderTop: "1px solid var(--border)", background: "var(--bg)" }}>
            <button
              onClick={() => setOpen(false)}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1"
              style={{ color: "var(--text-secondary)" }}
              data-testid="whats-new-close"
            >
              <X className="w-3.5 h-3.5" /> Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
